import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  LayoutDashboard,
  Plug,
  Megaphone,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Eye,
  MousePointer,
  MessageSquare,
  ExternalLink,
  Settings,
  Loader2,
  Info,
  Clock,
  FileSpreadsheet,
  FileText,
} from 'lucide-react'
import { metricasApi, relatoriosApi, type VeiculoTop } from '../../services/integracoesApi'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { allPortals } from '../../constants/integracoes'
import { StatCard, Panel, Card, Badge, Button } from '../../design-system'

interface PlataformaResumo {
  id: number
  portal: string
  nomeExibicao: string
  ativo: boolean
  statusConexao: string
  ultimoTeste: string | null
  ultimoErro: string | null
  totalPublicados: number
  totalPendentes: number
  totalErros: number
  totalDestaque: number
  limiteAnuncios: number | null
  anunciosAtivos: number | null
  percentualUso: number | null
  totalVisualizacoes: number
  totalCliques: number
  totalMensagens: number
  planoNome: string | null
  planoVencimento: string | null
  ultimaSincronizacao: string | null
  conexaoOk: boolean
  limiteProximo: boolean
  planoVenceEmBreve: boolean
}

interface Alerta {
  tipo: string
  portal: string
  mensagem: string
  acao: string | null
  link: string | null
  dataCriacao: string
}

interface DashboardData {
  totalPlataformasConfiguradas: number
  totalPlataformasAtivas: number
  totalPlataformasConectadas: number
  totalPlataformasComErro: number
  totalAnunciosAtivos: number
  totalAnunciosPendentes: number
  totalAnunciosComErro: number
  totalVeiculosSemPublicacao: number
  plataformas: PlataformaResumo[]
  alertas: Alerta[]
}


export default function IntegracoesDashboard() {
  const { showApiError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<DashboardData | null>(null)
  const [topVeiculos, setTopVeiculos] = useState<VeiculoTop[]>([])
  const [periodoEvolucao] = useState(30)
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    fetchDashboard(controller.signal)
    fetchTopVeiculos(controller.signal)
    return () => controller.abort()
  }, [])

  const fetchDashboard = async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const response = await api.get('/integracoes/dashboard', { signal })
      setData(response.data)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchTopVeiculos = async (signal?: AbortSignal) => {
    try {
      const dados = await metricasApi.getTopVeiculos(5, signal)
      setTopVeiculos(dados)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        console.error('Erro ao buscar top veículos:', error)
      }
    }
  }

  const handleExportExcel = async () => {
    try {
      setExporting('excel')
      await relatoriosApi.exportarExcel(periodoEvolucao)
    } catch (error) {
      showApiError(error)
    } finally {
      setExporting(null)
    }
  }

  const handleExportPdf = async () => {
    try {
      setExporting('pdf')
      await relatoriosApi.exportarPdf(periodoEvolucao)
    } catch (error) {
      showApiError(error)
    } finally {
      setExporting(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'CONECTADO':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />
      case 'ERRO':
        return <XCircle className="w-5 h-5 text-rose-500" />
      default:
        return <Clock className="w-5 h-5 text-slate-400" />
    }
  }

  const getAlertaIcon = (tipo: string) => {
    switch (tipo) {
      case 'ERRO':
        return <XCircle className="w-5 h-5 text-rose-500" />
      case 'AVISO':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />
      default:
        return <Info className="w-5 h-5 text-sky-500" />
    }
  }

  const getAlertaBgColor = (tipo: string) => {
    switch (tipo) {
      case 'ERRO':
        return 'bg-rose-50 border-rose-200'
      case 'AVISO':
        return 'bg-amber-50 border-amber-200'
      default:
        return 'bg-sky-50 border-sky-200'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500">Erro ao carregar dashboard</p>
        <button
          onClick={() => fetchDashboard()}
          className="mt-4 font-semibold text-primary-600 hover:text-primary-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900">
            <LayoutDashboard className="w-7 h-7 text-primary-600" />
            Dashboard de Integrações
          </h1>
          <p className="mt-1 text-slate-500">
            Gerencie suas integrações com plataformas de anúncios
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleExportExcel}
            disabled={exporting !== null}
            title="Exportar Excel"
          >
            {exporting === 'excel' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Excel</span>
          </Button>
          <Button
            variant="secondary"
            onClick={handleExportPdf}
            disabled={exporting !== null}
            title="Exportar PDF"
          >
            {exporting === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Button variant="secondary" onClick={() => fetchDashboard()}>
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Plataformas"
          value={`${data.totalPlataformasConectadas}/${data.totalPlataformasConfiguradas}`}
          subtitle={data.totalPlataformasComErro > 0 ? `${data.totalPlataformasComErro} com erro` : undefined}
          alert={data.totalPlataformasComErro > 0}
          icon={<Plug className="h-[18px] w-[18px]" />}
          delay={0}
        />
        <StatCard
          title="Anúncios Ativos"
          value={data.totalAnunciosAtivos}
          subtitle={data.totalAnunciosPendentes > 0 ? `${data.totalAnunciosPendentes} pendentes` : undefined}
          tone="positive"
          icon={<Megaphone className="h-[18px] w-[18px]" />}
          delay={60}
        />
        <StatCard
          title="Sem Publicação"
          value={data.totalVeiculosSemPublicacao}
          subtitle="Veículos disponíveis"
          icon={<AlertTriangle className="h-[18px] w-[18px]" />}
          delay={120}
        />
        <StatCard
          title="Com Erro"
          value={data.totalAnunciosComErro}
          subtitle="Publicações com problema"
          tone={data.totalAnunciosComErro > 0 ? 'negative' : 'default'}
          icon={<XCircle className="h-[18px] w-[18px]" />}
          delay={180}
        />
      </div>

      {/* Top Veículos */}
      {topVeiculos.length > 0 && (
        <Panel title="Top 5 Veículos Mais Visualizados" icon={<TrendingUp className="h-4 w-4" />}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="text-left py-2 px-3 font-bold">Veículo</th>
                  <th className="text-right py-2 px-3 font-bold">Visualizações</th>
                  <th className="text-right py-2 px-3 font-bold">Cliques</th>
                  <th className="text-right py-2 px-3 font-bold">Mensagens</th>
                  <th className="text-right py-2 px-3 font-bold">Plataformas</th>
                </tr>
              </thead>
              <tbody>
                {topVeiculos.map((v) => (
                  <tr key={v.veiculoId} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-3">
                        {v.fotoPrincipal ? (
                          <img
                            src={v.fotoPrincipal}
                            alt={v.descricao}
                            className="w-12 h-12 rounded object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                            <span className="text-slate-400 text-xs">Sem foto</span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-800">{v.descricao}</p>
                          <p className="text-xs text-slate-400">
                            {v.anoModelo} • R$ {v.valorVenda?.toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-2 px-3 font-semibold tabular-nums text-slate-900">
                      {v.totalVisualizacoes.toLocaleString('pt-BR')}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold tabular-nums text-slate-900">
                      {v.totalCliques.toLocaleString('pt-BR')}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold tabular-nums text-slate-900">
                      {v.totalMensagens.toLocaleString('pt-BR')}
                    </td>
                    <td className="text-right py-2 px-3">
                      <Badge tone="neutral">{v.qtdePlataformas}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* Métricas de Desempenho */}
      {data.plataformas.some(p => p.totalVisualizacoes > 0 || p.totalCliques > 0 || p.totalMensagens > 0) && (
        <Panel title="Métricas de Desempenho" icon={<TrendingUp className="h-4 w-4" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-xl bg-slate-50 ring-1 ring-slate-100">
              <Eye className="w-7 h-7 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-extrabold tracking-tight tabular-nums text-slate-900">
                {data.plataformas.reduce((sum, p) => sum + p.totalVisualizacoes, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-slate-500">Visualizações Totais</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-50 ring-1 ring-slate-100">
              <MousePointer className="w-7 h-7 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-extrabold tracking-tight tabular-nums text-slate-900">
                {data.plataformas.reduce((sum, p) => sum + p.totalCliques, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-slate-500">Cliques Totais</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-slate-50 ring-1 ring-slate-100">
              <MessageSquare className="w-7 h-7 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-extrabold tracking-tight tabular-nums text-slate-900">
                {data.plataformas.reduce((sum, p) => sum + p.totalMensagens, 0).toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-slate-500">Mensagens/Leads</p>
            </div>
          </div>
          {/* Comparativo por Plataforma */}
          <div className="mt-6">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Comparativo por Plataforma</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    <th className="text-left py-2 px-3 font-bold">Plataforma</th>
                    <th className="text-right py-2 px-3 font-bold">Visualizações</th>
                    <th className="text-right py-2 px-3 font-bold">Cliques</th>
                    <th className="text-right py-2 px-3 font-bold">Mensagens</th>
                    <th className="text-right py-2 px-3 font-bold">Taxa Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plataformas
                    .filter(p => p.totalVisualizacoes > 0 || p.totalCliques > 0 || p.totalMensagens > 0)
                    .sort((a, b) => b.totalVisualizacoes - a.totalVisualizacoes)
                    .map(p => (
                      <tr key={p.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                        <td className="py-2 px-3 font-semibold text-slate-800">{p.nomeExibicao || p.portal}</td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-600">{p.totalVisualizacoes.toLocaleString('pt-BR')}</td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-600">{p.totalCliques.toLocaleString('pt-BR')}</td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-600">{p.totalMensagens.toLocaleString('pt-BR')}</td>
                        <td className="text-right py-2 px-3 tabular-nums text-slate-600">
                          {p.totalVisualizacoes > 0
                            ? `${((p.totalMensagens / p.totalVisualizacoes) * 100).toFixed(2)}%`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      )}

      {/* Cards de Plataformas */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-bold text-slate-900">Plataformas</h2>
          <Link to="/integracoes/plataformas" className="text-sm font-bold text-primary-600 hover:text-primary-700">
            Ver todas
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {allPortals.map((portal) => {
            const plataforma = data.plataformas.find(p => p.portal === portal.id)
            const isConfigurado = !!plataforma

            return (
              <Card key={portal.id} className={`p-4 ${!isConfigurado ? 'opacity-60' : ''}`}>
                <div>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <PortalLogo portal={portal.id} size="md" />
                      <div>
                        <h3 className="font-bold text-slate-900">
                          {plataforma?.nomeExibicao || portal.nome}
                        </h3>
                        <div className="flex items-center gap-1">
                          {isConfigurado ? (
                            <>
                              {getStatusIcon(plataforma.statusConexao)}
                              <span className="text-xs text-slate-500">
                                {plataforma.statusConexao === 'CONECTADO' ? 'Conectado' :
                                 plataforma.statusConexao === 'ERRO' ? 'Erro' : 'Não testado'}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">Não configurado</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/integracoes/plataformas/${portal.id.toLowerCase()}`}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <Settings className="w-5 h-5" />
                    </Link>
                  </div>

                  {isConfigurado ? (
                    <>
                      {/* Estatísticas */}
                      <div className="grid grid-cols-3 gap-2 text-center mb-4">
                        <div>
                          <p className="text-lg font-bold tabular-nums text-emerald-600">{plataforma.totalPublicados}</p>
                          <p className="text-xs text-slate-500">Ativos</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-amber-600">{plataforma.totalPendentes}</p>
                          <p className="text-xs text-slate-500">Pendentes</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold tabular-nums text-rose-600">{plataforma.totalErros}</p>
                          <p className="text-xs text-slate-500">Erros</p>
                        </div>
                      </div>

                      {/* Barra de limite */}
                      {plataforma.limiteAnuncios && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Uso do limite</span>
                            <span className="tabular-nums">{plataforma.anunciosAtivos || 0}/{plataforma.limiteAnuncios}</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                (plataforma.percentualUso || 0) >= 90 ? 'bg-rose-500' :
                                (plataforma.percentualUso || 0) >= 80 ? 'bg-amber-500' :
                                'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(plataforma.percentualUso || 0, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Métricas */}
                      <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-1" title="Visualizações">
                          <Eye className="w-3 h-3" />
                          <span className="tabular-nums">{plataforma.totalVisualizacoes}</span>
                        </div>
                        <div className="flex items-center gap-1" title="Cliques">
                          <MousePointer className="w-3 h-3" />
                          <span className="tabular-nums">{plataforma.totalCliques}</span>
                        </div>
                        <div className="flex items-center gap-1" title="Mensagens">
                          <MessageSquare className="w-3 h-3" />
                          <span className="tabular-nums">{plataforma.totalMensagens}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-xs text-slate-400 mb-2">{portal.descricao}</p>
                      <Link
                        to={`/integracoes/plataformas/${portal.id.toLowerCase()}`}
                        className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                      >
                        Configurar →
                      </Link>
                    </div>
                  )}
                </div>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { to: '/integracoes/publicacoes', icon: Megaphone, titulo: 'Gerenciar Publicações', desc: 'Publicar veículos nos portais' },
          { to: '/integracoes/sincronizacao', icon: RefreshCw, titulo: 'Sincronização', desc: 'Importar anúncios das plataformas' },
          { to: '/integracoes/xml-feed', icon: TrendingUp, titulo: 'XML Feed', desc: 'Configurar feed automático' },
        ].map(({ to, icon: Icon, titulo, desc }) => (
          <Link key={to} to={to} className="group">
            <Card hoverable className="p-4 flex items-center gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100">
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{titulo}</h3>
                <p className="text-sm text-slate-400">{desc}</p>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Alertas */}
      {data.alertas.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold text-slate-900">Alertas</h2>
          <div className="space-y-2">
            {data.alertas.slice(0, 5).map((alerta, index) => (
              <div
                key={index}
                className={`flex items-center gap-4 p-4 rounded-2xl border ${getAlertaBgColor(alerta.tipo)}`}
              >
                {getAlertaIcon(alerta.tipo)}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{alerta.mensagem}</p>
                  {alerta.acao && (
                    <p className="text-xs text-slate-600 mt-1">{alerta.acao}</p>
                  )}
                </div>
                {alerta.link && (
                  <Link to={alerta.link} className="text-primary-600 hover:text-primary-700">
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
